













































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";

// Types
import { IClient, IClientAddressForm } from "@/models/clients";
import { IAddress, IAddressForm } from "@/models/addresses";
import { IState } from "@/store";

@Component({
  components: {
    "add-edit-address-form": () =>
      import("@/components/app/global/address/addEditAddressForm.vue")
  },
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS_ADDRESSES}/clientScope`](
          this.clientId
        );
      },
      params(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS_ADDRESSES}/params`](
          this.clientId
        );
      }
    })
  }
})
export default class AddEditClientAddressModal extends Mixins(
  RequestPromises,
  HasValidation,
  BrandsStore
) {
  // Props

  @Prop({ default: null })
  public data: any;

  @Prop({ default: ActionTypes.CREATE })
  public actionType!: string;

  @Prop({ default: "" })
  public addressId!: IAddress["id"];

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: "" })
  public searchedValue!: string;

  // Vuex Bindings

  private dataScope!: string;
  private params!: object;

  // Data

  public form = {
    name: "",
    address_1: "",
    address_2: "",
    country_id: "",
    region_id: null,
    city: "",
    postcode: "",
    type: 1,
    default: false
  } as IClientAddressForm;

  public formClone = {} as IClientAddressForm;
  public isLoading = true;
  public isProcessing = false;

  // Methods

  public created() {
    if (this.actionType === ActionTypes.UPDATE || this.data !== null) {
      this.form = this.$_.merge(this.form, this.data);
      this.formClone = this.$_.clone(this.form);
    }
    this.isLoading = false;
  }

  public get formIsValid() {
    if (
      this.$_.isEmpty(this.form.address_1) &&
      this.$_.isEmpty(this.form.postcode) &&
      this.$_.isEmpty(this.form.city) &&
      this.form.country_id !== ""
    )
      return false;

    if (
      this.actionType === ActionTypes.UPDATE &&
      (this.$_.isEqual(this.form, this.formClone) ||
        this.$_.isEmpty(this.form.name))
    )
      return false;

    return true;
  }

  public get formIsDisabled() {
    return (
      this.isProcessing || !this.formIsValid || this.hasErrors || this.isLoading
    );
  }

  public async validateBeforeSubmit() {
    this.isProcessing = true;
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      // Create or Update Phone
      const address =
        this.actionType === ActionTypes.CREATE
          ? await this.createAddress()
          : await this.updateAddress();
      // Refresh list (if applicable)
      if (!this.$_.isEmpty(this.params)) await this.refreshList();
      // Emit event(s)
      this.$emit("success", address);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  private async createAddress(): Promise<IAddress> {
    this.form.name = this.form.address_1;
    const address = await this.$store.dispatch(
      `data/${DataModules.CLIENTS_ADDRESSES}/create`,
      {
        id: this.clientId,
        data: this.form
      }
    );
    // Open success toast
    this.$store.dispatch("toast/show", {
      message: this.$t("_.object_successfully_added", {
        object: this.$t("_.address")
      })
    });

    return address;
  }

  private async updateAddress(): Promise<IAddress> {
    const address = await this.$store.dispatch(
      `data/${DataModules.CLIENTS_ADDRESSES}/update`,
      {
        id: this.clientId,
        address_id: this.addressId,
        data: this.getNewValues()
      }
    );
    // Open success toast
    this.$store.dispatch("toast/show", {
      message: this.$t("_.object_successfully_updated", {
        object: this.$t("_.address")
      })
    });

    return address;
  }

  public getNewValues() {
    return this.$_.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });
  }

  public onResult(result: IAddressForm) {
    this.$_.merge(this.form, result);
  }

  public async refreshList() {
    await this.$store.dispatch(`data/${DataModules.CLIENTS_ADDRESSES}/list`, {
      id: this.clientId,
      params: this.params,
      scope: this.dataScope,
      ignoreStored: true,
      vm: this
    });

    await this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_ADDRESSES,
      scope: this.dataScope,
      vm: this
    });
  }
}
